import TextInput from "@components/text_input"
import { useScriptsPageContext } from "@contexts/main/scripts_page_context"
import { oneTimesUpdate } from "@domain/coupons"
import { yupResolver } from "@hookform/resolvers/yup"
import useControlledForm from "@hooks/use_controlled_form"
import { Grid, LinearProgress, Paper, Stack, Typography } from "@mui/material"
import HighlightedMDEditor from "@pages/main/scripts/prompts/sub_components/HighlightedMDEditor"
import PlaygroundDrawer from "@pages/main/scripts/prompts/sub_components/playground/PlaygroundDrawer"
import PromptActionBar from "@pages/main/scripts/prompts/sub_components/PromptActionBar"
import PromptBulkEditDialog from "@pages/main/scripts/prompts/sub_components/PromptBulkEditDialog"
import { PromptFilters } from "@pages/main/scripts/prompts/sub_components/PromptFilters"
import PromptImportExportDialog from "@pages/main/scripts/prompts/sub_components/PromptImportExportDialog"
import {
  ALL_LAYERS,
  PromptLayerBar,
  type LayerFilter
} from "@pages/main/scripts/prompts/sub_components/PromptLayerBar"
import { PromptLayerBanner } from "@pages/main/scripts/prompts/sub_components/PromptLayerBanner"
import { PromptList } from "@pages/main/scripts/prompts/sub_components/PromptList"
import ResponseSchemaBuilder, {
  BaseResponseSchema
} from "@pages/main/scripts/prompts/sub_components/ResponseSchemaBuilder"
import TemplateLegend from "@pages/main/scripts/prompts/sub_components/TemplateLegend"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import { OneTimesType } from "__generated__/graphql"
import { useEffect, useMemo, useState } from "react"
import { Controller } from "react-hook-form"
import * as yup from "yup"

export {
  AiModels,
  AiPromptUsage,
  AiRoleEnum,
  GeminiModels,
  GPTModels
} from "@pages/main/scripts/prompts/prompt.models"

import {
  AiPromptUsage,
  AiRoleEnum,
  ALL_AI_ROLES,
  BASE_LAYER,
  BASE_LAYER_LABEL,
  compareEngineVersions,
  draftDiffersFromSaved,
  forkTargetsFor,
  formatModelPrice,
  GeminiModels,
  GPTModels,
  isGeminiOnlyPrompt,
  layersInUse,
  layersOfPrompt,
  promptLayerLabel,
  promptRoleLabel,
  PROMPT_MAX_OUTPUT_TOKENS,
  PROMPT_MIN_OUTPUT_TOKENS,
  promptReasoningEffortsFor,
  promptRowKey,
  SHARED_ROLE_LABEL,
  toModelSettingsPayload,
  type AiModels,
  type AiPromptModelSettings,
  type PromptLayer
} from "@pages/main/scripts/prompts/prompt.models"

export interface IAiPrompts {
  name: string
  description: string
  /** null = the shared row every role falls back to. */
  role?: AiRoleEnum | null
  /** null = the BASE layer every engine version falls back to. */
  engineVersion?: PromptLayer
  usage: AiPromptUsage
  createdAt: Date
  updatedAt: Date
  versions?: PromptVersion[]
  aiModel: AiModels
  responseSchema?: BaseResponseSchema | null
  modelSettings?: AiPromptModelSettings | null
}

interface PromptVersion extends IAiPrompts {}

function toModelSettingsForm(
  settings?: AiPromptModelSettings | null
): AiPromptModelSettings {
  return {
    reasoningEffort: settings?.reasoningEffort ?? null,
    maxOutputTokens: settings?.maxOutputTokens ?? null
  }
}

export type IAiPromptsInput = Omit<
  IAiPrompts,
  "createdAt" | "updatedAt" | "versions"
>

function toPromptInput(
  prompt: IAiPrompts | PromptVersion | null | undefined
): IAiPromptsInput | null {
  if (!prompt) return null

  return {
    name: prompt.name,
    description: prompt.description,
    role: prompt.role ?? null,
    engineVersion: prompt.engineVersion ?? null,
    usage: prompt.usage,
    aiModel: prompt.aiModel,
    responseSchema: prompt.responseSchema ?? null,
    modelSettings: toModelSettingsForm(prompt.modelSettings)
  }
}

export type FilterFormValues = {
  search: string
  role: "" | AiRoleEnum | "SHARED"
  usage: "" | AiPromptUsage
  aiModel: AiModels
  aiModelType: string // Can be "", "GPT", "GEMINI", or specific model like "gpt-4.1"
  hasResponseSchema: boolean
}

const EMPTY: IAiPromptsInput = {
  name: "",
  description: "",
  role: null,
  engineVersion: null,
  usage: undefined as unknown as AiPromptUsage,
  aiModel: GPTModels.GPT_4_1 as AiModels,
  responseSchema: null,
  modelSettings: toModelSettingsForm(null)
}

function estimatePromptTokens(text: string) {
  const asciiSegments: string[] = text.match(/[A-Za-z0-9_]+/g) ?? []
  const asciiTokens = asciiSegments.reduce(
    (total: number, segment: string) => total + Math.ceil(segment.length / 4),
    0
  )
  const nonAsciiChars = Array.from(text).filter((char) => {
    return char.charCodeAt(0) > 127
  }).length
  const punctuationChars = Array.from(text).filter((char) => {
    return /[^\sA-Za-z0-9_]/.test(char) && char.charCodeAt(0) <= 127
  }).length

  return Math.max(
    0,
    asciiTokens + nonAsciiChars + Math.ceil(punctuationChars / 2)
  )
}

export default function PromptsPage() {
  const { translate } = useScriptsPageContext()
  const [prompts, setPrompts] = useState<IAiPrompts[]>([])
  const [selectedPrompt, setSelectedPrompt] = useState<IAiPrompts | null>(null)
  const [loadingList, setLoadingList] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [openImportExport, setOpenImportExport] = useState(false)
  const [openPlayground, setOpenPlayground] = useState(false)
  const [engineVersions, setEngineVersions] = useState<string[]>([])
  const [layerFilter, setLayerFilter] = useState<LayerFilter>(null)
  const [forking, setForking] = useState(false)
  const [bulkMode, setBulkMode] = useState(false)
  const [bulkSelectedKeys, setBulkSelectedKeys] = useState<Set<string>>(
    () => new Set()
  )
  const [openBulkEdit, setOpenBulkEdit] = useState(false)

  const schema = yup.object({
    name: yup.string().required(),
    description: yup.string().required(),
    role: yup
      .mixed<AiRoleEnum>()
      .nullable()
      .test(
        "is-known-role-or-shared",
        "Unknown AI role",
        (v) => v == null || ALL_AI_ROLES.includes(v)
      ),
    engineVersion: yup
      .string()
      .nullable()
      .test(
        "is-engine-version-or-base",
        "Unknown engine version",
        (v) => v == null || /^v\d+$/.test(v)
      ),
    usage: yup.string().oneOf(Object.values(AiPromptUsage)).required(),
    aiModel: yup
      .string()
      .oneOf([...Object.values(GPTModels), ...Object.values(GeminiModels)])
      .required(),
    responseSchema: yup
      .mixed<BaseResponseSchema>()
      .nullable()
      .test(
        "is-object-or-null",
        "Invalid JSON object",
        (v) => v === null || typeof v === "object"
      ),
    modelSettings: yup
      .mixed<AiPromptModelSettings>()
      .nullable()
      .test(
        "max-output-tokens-range",
        `Max output tokens must be an integer between ${PROMPT_MIN_OUTPUT_TOKENS} and ${PROMPT_MAX_OUTPUT_TOKENS}`,
        (v) => {
          const tokens = v?.maxOutputTokens
          if (tokens === null || tokens === undefined || String(tokens) === "")
            return true
          const value = Number(tokens)
          return (
            Number.isInteger(value) &&
            value >= PROMPT_MIN_OUTPUT_TOKENS &&
            value <= PROMPT_MAX_OUTPUT_TOKENS
          )
        }
      )
  })

  const {
    control: filterControl,
    watch: watchFilter,
    reset: resetFilter
  } = useControlledForm<FilterFormValues>({
    mode: "all",
    defaultValues: {
      search: "",
      role: "",
      usage: "",
      aiModel: GPTModels.GPT_4_1 as AiModels,
      aiModelType: "",
      hasResponseSchema: false
    }
  })

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: { isValid, isDirty }
  } = useControlledForm<IAiPromptsInput>({
    resolver: yupResolver(schema),
    mode: "all",
    values: selectedPrompt
      ? {
          name: selectedPrompt.name,
          description: selectedPrompt.description,
          role: selectedPrompt.role ?? null,
          engineVersion: selectedPrompt.engineVersion ?? null,
          usage: selectedPrompt.usage,
          aiModel: selectedPrompt.aiModel,
          responseSchema: selectedPrompt.responseSchema,
          modelSettings: toModelSettingsForm(selectedPrompt.modelSettings)
        }
      : EMPTY
  })

  const selectedModel = watch("aiModel")
  const reasoningEfforts = promptReasoningEffortsFor(selectedModel ?? "")
  useEffect(() => {
    const effort = watch("modelSettings.reasoningEffort")
    if (effort && !reasoningEfforts.includes(effort)) {
      setValue("modelSettings.reasoningEffort", null, { shouldDirty: true })
    }
  }, [selectedModel])

  const [openFc, setOpenFc] = useState(false)
  const draftValues = watch()
  const responseSchema = watch("responseSchema")
  const promptText = watch("description") ?? ""
  const promptStats = useMemo(
    () => ({
      characters: Array.from(promptText).length,
      tokens: estimatePromptTokens(promptText)
    }),
    [promptText]
  )
  const hasChanges = selectedPrompt
    ? isDirty && draftDiffersFromSaved(selectedPrompt, draftValues)
    : isDirty
  const draftPrompt = selectedPrompt
    ? ({
        ...selectedPrompt,
        ...draftValues,
        responseSchema: draftValues.responseSchema ?? null
      } as IAiPrompts)
    : null

  /** BASE plus every layer that has a row, plus every version the service runs but has not forked. */
  const knownLayers = useMemo<PromptLayer[]>(() => {
    const present = layersInUse(prompts)
    const missing = engineVersions.filter((v) => !present.includes(v))
    return [...present, ...missing]
  }, [prompts, engineVersions])

  const layerPrompts = useMemo(
    () =>
      layerFilter === ALL_LAYERS
        ? prompts
        : prompts.filter((p) => (p.engineVersion ?? null) === layerFilter),
    [prompts, layerFilter]
  )

  /** The same prompt on the other layers — "did my v2 edit actually diverge from BASE?". */
  const forkTargets = useMemo(
    () =>
      selectedPrompt
        ? forkTargetsFor(
            selectedPrompt.engineVersion ?? null,
            engineVersions,
            layersOfPrompt(prompts, selectedPrompt.name)
          )
        : [],
    [selectedPrompt, engineVersions, prompts]
  )

  const layerSiblings = useMemo(
    () =>
      selectedPrompt
        ? prompts
            .filter(
              (p) =>
                p.name === selectedPrompt.name &&
                (p.role ?? null) === (selectedPrompt.role ?? null) &&
                (p.engineVersion ?? null) !==
                  (selectedPrompt.engineVersion ?? null)
            )
            .sort((a, b) =>
              compareEngineVersions(
                a.engineVersion ?? "v0",
                b.engineVersion ?? "v0"
              )
            )
        : [],
    [prompts, selectedPrompt]
  )

  const countForLayer = (layer: LayerFilter) =>
    layer === ALL_LAYERS
      ? prompts.length
      : prompts.filter((p) => (p.engineVersion ?? null) === layer).length

  const bulkSelectedPrompts = useMemo(
    () => prompts.filter((p) => bulkSelectedKeys.has(promptRowKey(p))),
    [prompts, bulkSelectedKeys]
  )

  function toggleBulkKeys(keys: string[], checked: boolean) {
    setBulkSelectedKeys((prev) => {
      const next = new Set(prev)
      keys.forEach((key) => (checked ? next.add(key) : next.delete(key)))
      return next
    })
  }

  function toggleBulkMode() {
    setBulkMode((on) => !on)
    setBulkSelectedKeys(new Set())
  }

  function openBulkEditDialog() {
    // Saving the editor afterwards would send its stale aiModel/usage and undo the bulk change.
    if (
      hasChanges &&
      !window.confirm(
        "Editördeki kaydedilmemiş değişiklikler kaybolacak. Devam edilsin mi?"
      )
    ) {
      return
    }
    setOpenBulkEdit(true)
  }

  async function refetchAfterBulkEdit() {
    const list = await fetchPrompts()
    if (!list || !selectedPrompt) return
    const key = promptRowKey(selectedPrompt)
    setSelectedPrompt(list.find((p) => promptRowKey(p) === key) ?? null)
  }

  type SortMode = "NAME" | "VERSION_DATE"
  const [sortMode, setSortMode] = useState<SortMode>("NAME")

  async function fetchPrompts() {
    setLoadingList(true)
    const { isSuccess, errorMessage, data } = await oneTimesUpdate({
      type: OneTimesType.GetAllAiPrompts,
      metaData: {}
    })
    setLoadingList(false)

    if (!isSuccess || !data) {
      showErrorToast(String(errorMessage))
      return
    }
    setPrompts(data.data as IAiPrompts[])
    return data.data as IAiPrompts[]
  }

  /**
   * The layers a prompt may be forked to are the versions the AI service actually runs, not
   * whatever the catalogue happens to contain — offering a layer no engine reads would create
   * rows nothing ever serves.
   */
  async function fetchEngineVersions() {
    const { isSuccess, data } = await oneTimesUpdate({
      type: OneTimesType.GetAiEngineVersions,
      metaData: {}
    })
    if (!isSuccess || !data) return
    const summaries = (data.data ?? []) as { version: string }[]
    setEngineVersions(
      summaries.map((summary) => summary.version).sort(compareEngineVersions)
    )
  }

  useEffect(() => {
    fetchPrompts()
    fetchEngineVersions()
  }, [])

  async function handlePrompt(params: IAiPromptsInput) {
    const isUpdate = !!selectedPrompt
    const nextRole = params.role ?? null
    const currentRole = selectedPrompt?.role ?? null
    const nextLayer = params.engineVersion ?? null
    const currentLayer = selectedPrompt?.engineVersion ?? null
    const isRoleMove = isUpdate && nextRole !== currentRole
    const isLayerMove = isUpdate && nextLayer !== currentLayer

    // The move target must be free — (name, role, engineVersion) is unique, so the row cannot
    // land on top of an existing one. Checked here too so the admin sees it before the round trip.
    if (
      (isRoleMove || isLayerMove) &&
      prompts.some(
        (p) =>
          p.name === selectedPrompt?.name &&
          (p.role ?? null) === nextRole &&
          (p.engineVersion ?? null) === nextLayer
      )
    ) {
      showErrorToast(
        `${selectedPrompt?.name} already has a ${promptRoleLabel(
          nextRole
        )} row on ${promptLayerLabel(nextLayer)}`
      )
      return false
    }

    if (isRoleMove || isLayerMove) {
      const confirmed = window.confirm(
        `${selectedPrompt?.name} promptu ${promptRoleLabel(
          currentRole
        )} / ${promptLayerLabel(currentLayer)} konumundan ${promptRoleLabel(
          nextRole
        )} / ${promptLayerLabel(
          nextLayer
        )} konumuna TAŞINACAK (kopyalanmayacak). Onaylıyor musunuz?`
      )
      if (!confirmed) return false
    }

    const body = {
      ...params,
      modelSettings: toModelSettingsPayload(
        params.aiModel,
        params.modelSettings
      )
    }

    try {
      setSubmitting(true)
      const { isSuccess, errorMessage } = await oneTimesUpdate({
        type: isUpdate
          ? OneTimesType.UpdateAiPrompt
          : OneTimesType.CreateAiPrompt,
        // A row is identified by (name, role, engineVersion): editing v2's copy must not touch
        // the BASE row that carries the same name. On update those three are the identity and
        // `newRole`/`newEngineVersion` are where the row should end up, so changing a select
        // MOVES the row instead of editing whichever row already sits there.
        metaData: isUpdate
          ? {
              ...body,
              name: selectedPrompt?.name,
              role: currentRole,
              engineVersion: currentLayer,
              newRole: nextRole,
              newEngineVersion: nextLayer
            }
          : { ...body, role: nextRole, engineVersion: nextLayer }
      })

      if (!isSuccess) {
        showErrorToast(String(errorMessage))
        return false
      }

      const list = await fetchPrompts()
      if (!list) {
        showErrorToast("Failed to fetch prompts")
        return false
      }

      const saved = list.find(
        (p) =>
          promptRowKey(p) ===
          promptRowKey({
            name: selectedPrompt?.name ?? params.name,
            role: nextRole,
            engineVersion: nextLayer
          })
      )
      if (saved) {
        setSelectedPrompt(saved)
        reset(toPromptInput(saved) ?? EMPTY)
      }
      return true
    } catch (error) {
      showErrorToast("Unexpected error: " + String(error))
      return false
    } finally {
      setSubmitting(false)
    }
  }

  function loadPromptVersion(version: IAiPromptsInput) {
    reset(version, {
      keepDirtyValues: false,
      keepDefaultValues: false
    })
  }

  async function rollbackPromptVersion(
    version: IAiPromptsInput,
    label: string
  ) {
    if (!selectedPrompt) return

    const confirmed = window.confirm(
      `${selectedPrompt.name} promptunu ${label} versiyonuna rollback yapmak istediğinize emin misiniz?`
    )

    if (!confirmed) return

    const ok = await handlePrompt(version)
    if (ok) {
      showSuccessToast(
        `${selectedPrompt.name} ${label} versiyonuna rollback edildi`
      )
    }
  }

  async function deletePrompt(prompt: IAiPrompts) {
    setDeleting(promptRowKey(prompt))
    const { isSuccess, errorMessage } = await oneTimesUpdate({
      type: OneTimesType.DeleteAiPrompt,
      metaData: {
        name: prompt.name,
        role: prompt.role ?? null,
        engineVersion: prompt.engineVersion ?? null
      }
    })
    setDeleting(null)

    if (!isSuccess) {
      showErrorToast(String(errorMessage))
      return
    }

    setSelectedPrompt(null)
    reset(EMPTY)

    fetchPrompts()
  }

  /**
   * Copies EVERY row of this prompt name — the shared row and each role override — into another
   * engine layer, in one call on the AI service.
   *
   * A whole name at once, never one row: the AI ranks the layer above the role, so a lone forked
   * shared row would shadow a role override still sitting on BASE, and that role would quietly
   * start answering with text nobody chose for it.
   */
  async function forkToEngineVersion(target: string) {
    if (!selectedPrompt) return

    const source = selectedPrompt.engineVersion ?? null
    const siblings = prompts.filter(
      (p) =>
        p.name === selectedPrompt.name && (p.engineVersion ?? null) === source
    )

    const confirmed = window.confirm(
      `${selectedPrompt.name} — ${promptLayerLabel(source)} katmanındaki ` +
        `${siblings.length} satır ${target} katmanına KOPYALANACAK. ` +
        `${promptLayerLabel(source)} olduğu gibi kalır; bundan sonra ${target} kendi ` +
        "kopyasını okur. Devam edilsin mi?"
    )
    if (!confirmed) return

    setForking(true)
    const { isSuccess, errorMessage } = await oneTimesUpdate({
      type: OneTimesType.ForkAiPromptToEngineVersion,
      metaData: {
        name: selectedPrompt.name,
        engineVersion: target,
        from: source
      }
    })
    setForking(false)

    if (!isSuccess) {
      showErrorToast(String(errorMessage))
      return
    }

    const list = await fetchPrompts()
    const created = list?.find(
      (p) =>
        p.name === selectedPrompt.name &&
        (p.role ?? null) === (selectedPrompt.role ?? null) &&
        (p.engineVersion ?? null) === target
    )
    if (created) {
      setLayerFilter(target)
      setSelectedPrompt(created)
    }
    showSuccessToast(
      `${selectedPrompt.name} forked to ${target} (${siblings.length} row(s))`
    )
  }

  useEffect(() => {
    reset(toPromptInput(selectedPrompt) ?? EMPTY, {
      keepDirtyValues: false,
      keepDefaultValues: false
    })
  }, [selectedPrompt, reset])

  return (
    <Grid container spacing={2} sx={{ height: "100%" }}>
      <Grid item xs={3} sx={{ height: "100%" }}>
        <Paper variant="outlined" sx={{ height: "100%", overflow: "auto" }}>
          {loadingList && <LinearProgress />}
          <PromptLayerBar
            layers={knownLayers}
            value={layerFilter}
            onChange={setLayerFilter}
            countFor={countForLayer}
          />
          <PromptFilters
            control={filterControl}
            resetFilter={resetFilter}
            sortMode={sortMode}
            setSortMode={setSortMode}
            onAddNew={() => {
              setSelectedPrompt(null)
              // A new row starts on the layer being browsed, so "add" on the v2 tab cannot
              // silently create a BASE row every engine version would then read.
              reset({
                ...EMPTY,
                engineVersion: layerFilter === ALL_LAYERS ? null : layerFilter
              })
            }}
            onRefetch={fetchPrompts}
            bulkMode={bulkMode}
            onToggleBulkMode={toggleBulkMode}
            onOpenImportExport={() => setOpenImportExport(true)}
          />
          <PromptList
            prompts={layerPrompts}
            allPrompts={prompts}
            showLayer={layerFilter === ALL_LAYERS}
            sortMode={sortMode}
            selectedPrompt={selectedPrompt}
            setSelectedPrompt={setSelectedPrompt}
            watchFilter={watchFilter}
            bulkMode={bulkMode}
            bulkSelectedKeys={bulkSelectedKeys}
            onBulkToggle={toggleBulkKeys}
            onBulkEdit={openBulkEditDialog}
          />
        </Paper>
      </Grid>

      <Grid
        item
        xs={9}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1
        }}
      >
        <Paper variant="outlined" sx={{ flex: 1, overflow: "auto", p: 1 }}>
          <Stack spacing={1} height="99%">
            {selectedPrompt && (
              <PromptLayerBanner
                layer={selectedPrompt.engineVersion ?? null}
                name={selectedPrompt.name}
                engineVersions={engineVersions}
                forkedInto={layersOfPrompt(prompts, selectedPrompt.name)}
              />
            )}
            <Stack spacing={1} direction="row">
              <TextInput
                label="Name"
                name="name"
                placeholder={translate("enter")}
                control={control}
                disabled={!!selectedPrompt} // update'de read-only
                InputProps={{ readOnly: !!selectedPrompt }}
              />
              <TextInput
                key={`engineVersion:${String(watch("engineVersion"))}`}
                type="select"
                label="Engine layer"
                name="engineVersion"
                control={control}
                placeholder={BASE_LAYER_LABEL}
                selectOptions={[
                  {
                    value: BASE_LAYER as unknown as string,
                    label: BASE_LAYER_LABEL
                  },
                  ...knownLayers
                    .filter((layer): layer is string => !!layer)
                    .map((layer) => ({ value: layer, label: layer }))
                ]}
              />
              <TextInput
                key={`role:${String(watch("role"))}`}
                type="select"
                label="Role"
                name="role"
                control={control}
                placeholder="Role"
                selectOptions={[
                  {
                    value: null as unknown as string,
                    label: SHARED_ROLE_LABEL
                  },
                  ...ALL_AI_ROLES.map((v) => ({ value: v, label: v }))
                ]}
              />

              <TextInput
                key={watch("usage")}
                type="select"
                placeholder="Usage"
                label="Usage"
                name="usage"
                control={control}
                selectOptions={Object.values(AiPromptUsage).map((v) => ({
                  value: v,
                  label: v
                }))}
              />
              <TextInput
                key={watch("aiModel")}
                type="select"
                label="AI Model"
                name="aiModel"
                control={control}
                placeholder="Select AI Model"
                selectOptions={
                  isGeminiOnlyPrompt(watch("name") ?? "")
                    ? Object.values(GeminiModels).map((v) => ({
                        value: v,
                        label: formatModelPrice(v),
                        icon: "💎"
                      }))
                    : [
                        ...Object.values(GPTModels).map((v) => ({
                          value: v,
                          label: formatModelPrice(v),
                          icon: "🤖"
                        })),
                        ...Object.values(GeminiModels).map((v) => ({
                          value: v,
                          label: formatModelPrice(v),
                          icon: "💎"
                        }))
                      ]
                }
              />
              {reasoningEfforts.length > 0 && (
                <TextInput
                  key={`${selectedModel}-${String(
                    watch("modelSettings.reasoningEffort")
                  )}`}
                  type="select"
                  label="Reasoning"
                  name="modelSettings.reasoningEffort"
                  control={control}
                  placeholder="Default"
                  selectOptions={[
                    {
                      value: null as unknown as string,
                      label: "Default (code)"
                    },
                    ...reasoningEfforts.map((v) => ({ value: v, label: v }))
                  ]}
                />
              )}
              <TextInput
                type="number"
                label="Max output tokens"
                name="modelSettings.maxOutputTokens"
                control={control}
                placeholder="Default (code)"
              />
            </Stack>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Stack spacing={0.5} sx={{ minHeight: 0, flex: 1 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                  >
                    <TemplateLegend />
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                    >
                      {promptStats.characters.toLocaleString()} chars • ~
                      {promptStats.tokens.toLocaleString()} tokens
                    </Typography>
                  </Stack>
                  <HighlightedMDEditor
                    height="100%"
                    value={field.value ?? ""}
                    onChange={(v) => field.onChange(v ?? "")}
                  />
                </Stack>
              )}
            />
          </Stack>
        </Paper>
        <PromptActionBar
          selectedPrompt={selectedPrompt}
          prompts={prompts}
          layerSiblings={layerSiblings}
          draft={toPromptInput(draftPrompt)}
          forkTargets={forkTargets}
          hasSchema={!!responseSchema}
          isDirty={hasChanges}
          canSave={isValid && hasChanges}
          canPlay={!!promptText.trim() && !!draftValues.aiModel}
          submitting={submitting}
          deleting={
            !!selectedPrompt && deleting === promptRowKey(selectedPrompt)
          }
          forking={forking}
          onOpenSchema={() => setOpenFc(true)}
          onRemoveSchema={() =>
            setValue("responseSchema", null, {
              shouldValidate: true,
              shouldDirty: true
            })
          }
          onOpenPlayground={() => setOpenPlayground(true)}
          onSave={handleSubmit(handlePrompt)}
          onDelete={() => {
            if (
              selectedPrompt &&
              window.confirm(
                `${selectedPrompt.name} (${promptRoleLabel(
                  selectedPrompt.role
                )} / ${promptLayerLabel(
                  selectedPrompt.engineVersion
                )}) silinsin mi?`
              )
            ) {
              deletePrompt(selectedPrompt)
            }
          }}
          onFork={forkToEngineVersion}
          onLoadVersion={loadPromptVersion}
          onRollbackVersion={rollbackPromptVersion}
        />
        <ResponseSchemaBuilder
          open={openFc}
          setOpen={setOpenFc}
          setValue={setValue}
          initialValue={responseSchema}
        />
        <PlaygroundDrawer
          open={openPlayground}
          onClose={() => setOpenPlayground(false)}
          promptName={draftValues.name || selectedPrompt?.name || ""}
          systemPrompt={promptText}
          model={draftValues.aiModel}
          modelSettings={toModelSettingsPayload(
            draftValues.aiModel,
            draftValues.modelSettings
          )}
          responseSchema={draftValues.responseSchema}
          isDirty={hasChanges}
          onSystemPromptChange={(value) =>
            setValue("description", value, {
              shouldDirty: true,
              shouldValidate: true
            })
          }
          onSave={handleSubmit(handlePrompt)}
          canSave={isValid && hasChanges}
          saving={submitting}
        />
        <PromptImportExportDialog
          open={openImportExport}
          onClose={() => setOpenImportExport(false)}
          prompts={prompts}
          onImported={fetchPrompts}
        />
        <PromptBulkEditDialog
          open={openBulkEdit}
          onClose={() => setOpenBulkEdit(false)}
          prompts={bulkSelectedPrompts}
          onApplied={refetchAfterBulkEdit}
        />
      </Grid>
    </Grid>
  )
}
